import { useLocation, useNavigate } from 'react-router-dom'

import { ArrowLeftIcon } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

// `isAncestorPath` lives in `@repo/ui` (packages/ui/utils/referrerPath.ts) so it's the
// SAME predicate `Breadcrumbs` uses to decide whether the parent segment should link to
// `backTo` instead of the bare route — see that file's comment for the full rationale.
import { isAncestorPath } from '@repo/ui'

type ReferrerState = {
  backTo?: string
  backLabel?: string
}

/**
 * Renders a "← Back to <label>" link ONLY when the current page was reached via a
 * click-through that stashed `{ backTo, backLabel }` in router state. On a direct visit
 * there is no such state, so this renders nothing.
 *
 * It ALSO renders nothing when the referrer is the page's own breadcrumb parent. Back
 * links exist for CROSS-surface references — Certificate → Product, Owner →
 * Certificate — where the destination's breadcrumb cannot describe where you came
 * from. For a plain drill-in (Certificates → a certificate) the breadcrumb already is
 * the way back, and showing both puts two controls with identical meaning side by side.
 */
/**
 * Will a back link actually render on this route?
 *
 * Exported because `Header` has to know, and it cannot ask the component: the
 * component self-suppresses on three separate conditions, so "the caller did not set
 * `hideReferrerBack`" is NOT the same question as "a link is on screen". Header uses
 * this to decide whether the wayfinding row has CONTENT, which changes whether the
 * header is lifted — get it wrong and a page with a back link is shifted 24.5px up,
 * putting the link 8px from the window edge.
 *
 * One predicate, two consumers, so the row's height and its contents cannot disagree.
 */
export const useHasReferrerBack = (): boolean => {
  const location = useLocation()
  const state = location.state as ReferrerState | null

  if (!state?.backTo || !state?.backLabel) return false
  // A link back to the page you are already on is not navigation. Reachable, not
  // theoretical: `isAncestorPath` answers false for EQUAL paths (it requires the
  // referrer to be strictly shorter), so /docs → /docs fell through every guard and
  // rendered "← Back to Docs" on the docs page itself, pointing at itself.
  if (state.backTo.split('?')[0] === location.pathname) return false
  // Redundant with the breadcrumb — the breadcrumb wins (it is the single back
  // affordance for hierarchical navigation).
  if (isAncestorPath(state.backTo, location.pathname)) return false
  return true
}

export const ReferrerBackLink = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const renders = useHasReferrerBack()

  const state = location.state as ReferrerState | null

  // Both conditions, even though `useHasReferrerBack` already implies the second.
  // The hook returns a boolean, so it narrows nothing for the compiler — extracting
  // the predicate moved the null checks out of this function's control flow and
  // `state.backTo` below became "possibly null". Two real TS18047s, and the honest
  // fix is to re-state the guard here rather than assert non-null: the hook and this
  // component read the same router state, so if they ever disagree the render should
  // bail, not crash.
  if (!renders || !state?.backTo || !state?.backLabel) return null

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={() => navigate(state.backTo as string)}
      sx={{
        cursor: 'pointer',
        width: 'fit-content',
        // No optical nudge. There used to be a translateY(3px) here, correcting for
        // sitting beside the large page title; the link now lives on the breadcrumb
        // row beside same-size text, where that offset just knocks it out of line.
        color: theme.palette.text.secondary,
        '&:hover': { color: theme.palette.text.primary },
      }}
    >
      <ArrowLeftIcon size={16} strokeWidth={1.5} />
      <Typography variant="body3">Back to {state.backLabel}</Typography>
    </Stack>
  )
}
